import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { wordpressAPI } from '../services/wordpressApi';
import { hubAPI } from '../services/hubApi';
import { programAPI } from '../services/programApi';
import { Program } from '../types/program';
import { eventAPI } from '../services/eventApi';
import { AppEvent } from '../types/event';
import { WPPost } from '../types/wordpress';
import { selectFeaturedHubs, Hub } from '../types/hub';
import Card from '../components/ui/Card';
import ResourceCard from '../components/resources/ResourceCard';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import ErrorState from '../components/ui/ErrorState';
import SearchAutocomplete from '../components/search/SearchAutocomplete';
import { buildResourcePath } from '../utils/resourceLinks';
import { AcademicCapIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useAccessLevel } from '../hooks/useAccessLevel';
import { useAuthModal } from '../components/auth/AuthModalContext';
import { placePath } from '../utils/placePaths';
import HubCard from '../components/hubs/HubCard';
import EventCard from '../components/events/EventCard';
import { readReopenSearch } from '../utils/pageFrom';
import { stripHtml } from '../utils/htmlEntities';
import PracticeHome from '../components/home/PracticeHome';
import NewsletterSignup from '../components/home/NewsletterSignup';
import {
  ORBCON_REPORT_CATEGORY_ID,
  ORBCON_REPORT_PATH,
  ORBCON_REPORT_TITLE,
  groupReportIssues,
} from '../content/orbconReport';

function PublicHome() {
  const location = useLocation();
  const { canViewElearning } = useAccessLevel();
  const { openAuthModal } = useAuthModal();
  const [featuredPosts, setFeaturedPosts] = useState<WPPost[]>([]);
  const [featuredHubs, setFeaturedHubs] = useState<Hub[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<AppEvent[]>([]);
  const [reportIssue, setReportIssue] = useState<{ label: string; posts: WPPost[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [hubsLoading, setHubsLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchFeaturedHubs = async () => {
      try {
        setHubsLoading(true);
        const hubs = await hubAPI.fetchHubs();
        setFeaturedHubs(selectFeaturedHubs(hubs));
      } catch (err) {
        console.warn('Failed to load featured hubs:', err);
        setFeaturedHubs([]);
      } finally {
        setHubsLoading(false);
      }
    };

    fetchFeaturedHubs();
    programAPI.fetchPrograms().then(setPrograms).catch(() => setPrograms([]));
  }, []);

  useEffect(() => {
    const fetchFeaturedPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        // Fetch latest 6 posts
        const posts = await wordpressAPI.fetchPosts({
          per_page: 6,
          orderby: 'date',
          order: 'desc',
        });
        setFeaturedPosts(posts);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load posts'));
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedPosts();
  }, []);

  useEffect(() => {
    let cancelled = false;
    wordpressAPI
      .fetchPosts({
        categories: [ORBCON_REPORT_CATEGORY_ID],
        per_page: 6,
        orderby: 'date',
        order: 'desc',
      })
      .then((posts) => {
        if (cancelled) return;
        setReportIssue(groupReportIssues(posts)[0] ?? null);
      })
      .catch(() => {
        if (!cancelled) setReportIssue(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchUpcomingEvents = async () => {
      try {
        setEventsLoading(true);
        const events = await eventAPI.fetchEvents();
        const now = new Date();
        const sortedEvents = events
          .map((event) => ({ event, startDate: new Date(event.startAt) }))
          .filter(({ startDate }) => startDate >= now)
          .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
          .slice(0, 3)
          .map(({ event }) => event);
        
        setUpcomingEvents(sortedEvents);
      } catch (err) {
        console.error('Failed to load events:', err);
      } finally {
        setEventsLoading(false);
      }
    };

    fetchUpcomingEvents();
  }, []);

  return (
    <>
      {/* Hero Section - Full Width Background */}
      <section
        className="relative w-full py-14 md:py-20 text-center bg-cover bg-center"
        style={{ backgroundImage: "url('/images/hero-background-1.png')" }}
      >
        <div className="absolute inset-0 bg-[#00467f]/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5">
              Resources for Transfusion Medicine Professionals
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Access guidelines, templates, training materials, and more
            </p>
            
            {/* Prominent Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <SearchAutocomplete
                className="w-full"
                size="large"
                initialQuery={readReopenSearch(location.state) ?? ''}
              />
            </div>

            {/* Quick Filters */}
            {programs.some((program) => program.kind !== 'REFERENCE') && (
              <div className="mt-8">
                <p className="text-sm font-medium text-white/60 mb-3">Programs</p>
                <div className="flex flex-wrap justify-center gap-2.5">
                  {programs.filter((program) => program.kind !== 'REFERENCE').map((program) => (
                    <Link
                      key={program.id}
                      to={placePath(program)}
                      className="px-4 py-2 bg-white/90 border border-white/30 rounded-full hover:bg-white text-sm font-medium text-gray-700"
                    >
                      {program.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <section className="bg-gray-50 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-[#00467f] pl-4">Upcoming Events</h2>
              <Link
                to="/events"
                className="text-primary hover:text-primary-dark font-medium"
              >
                View All Events →
              </Link>
            </div>

            {eventsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Card key={i}>
                    <LoadingSkeleton lines={3} />
                  </Card>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-6">
                {upcomingEvents.map((event) => (
                  <EventCard key={event.id} event={event} layout="home" />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Latest Resources */}
      <section className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-[#00467f] pl-4">Latest Resources</h2>
            <Link
              to="/resources"
              className="text-primary hover:text-primary-dark font-medium"
            >
              View All →
            </Link>
          </div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}>
                <LoadingSkeleton lines={4} />
              </Card>
            ))}
          </div>
        )}

        {error && (
          <ErrorState
            error={error}
            title="Failed to Load Resources"
            onRetry={() => window.location.reload()}
          />
        )}

        {!loading && !error && featuredPosts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredPosts.map((post) => (
              <ResourceCard key={post.id} post={post} />
            ))}
          </div>
        )}

        {!loading && !error && featuredPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No resources found.</p>
          </div>
        )}
        </div>
      </section>

      {/* Featured hubs */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-[#00467f] pl-4">Featured hubs</h2>
            <Link
              to="/resources"
              className="text-primary hover:text-primary-dark font-medium"
            >
              Programs
            </Link>
          </div>
          {hubsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <LoadingSkeleton key={i} lines={4} className="h-40" />
              ))}
            </div>
          ) : featuredHubs.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No featured toolkits yet.</p>
          ) : (
            <div className="flex flex-wrap gap-6">
              {featuredHubs.map((hub) => (
                <HubCard key={hub.id} hub={hub} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* eLearning Spotlight */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-[#00467f] pl-4">eLearning</h2>
            {canViewElearning ? (
              <Link
                to="/elearning"
                className="text-primary hover:text-primary-dark font-medium"
              >
                Browse Courses →
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => openAuthModal('sign-in')}
                className="text-primary hover:text-primary-dark font-medium"
              >
                Sign in to browse courses →
              </button>
            )}
          </div>
          <div className="flex flex-col md:flex-row items-center gap-8 bg-white rounded-xl border border-gray-200 shadow-sm p-8">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 bg-[#00467f]/10 rounded-2xl flex items-center justify-center">
                <AcademicCapIcon className="h-14 w-14 text-[#00467f]" />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Build Your Transfusion Medicine Knowledge</h3>
              <p className="text-gray-600 mb-4 max-w-2xl">
                Access self-paced courses, interactive modules, and quizzes designed for transfusion medicine professionals at every level.
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-3">
                {canViewElearning ? (
                  <>
                    <Link
                      to="/elearning"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#00467f] text-white rounded-lg font-medium hover:bg-[#003662] transition-colors"
                    >
                      <AcademicCapIcon className="h-5 w-5" />
                      Start Learning
                    </Link>
                    <Link
                      to="/elearning"
                      className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:border-[#00467f] hover:text-[#00467f] transition-colors"
                    >
                      View All Courses
                    </Link>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => openAuthModal('sign-up')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#00467f] text-white rounded-lg font-medium hover:bg-[#003662] transition-colors min-h-[44px]"
                  >
                    <AcademicCapIcon className="h-5 w-5" />
                    Sign in to start learning
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {reportIssue && (
        <section className="bg-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-[#00467f] pl-4">
                {ORBCON_REPORT_TITLE}
              </h2>
              <Link
                to={ORBCON_REPORT_PATH}
                className="text-primary hover:text-primary-dark font-medium"
              >
                All issues →
              </Link>
            </div>
            <Card>
              <p className="text-sm font-semibold text-gray-500 mb-3">{reportIssue.label}</p>
              <ul className="space-y-3">
                {reportIssue.posts.map((post) => (
                  <li key={post.id}>
                    <Link
                      to={buildResourcePath(post.slug)}
                      className="text-base font-medium text-gray-900 hover:text-primary focus-ring rounded-sm"
                    >
                      {stripHtml(post.title.rendered)}
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </section>
      )}

      {/* Stay Connected */}
      <section className="bg-[#00467f] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <EnvelopeIcon className="h-12 w-12 text-white/80 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-3 text-center">Stay Connected</h2>
            <p className="text-blue-100 mb-6 text-center">
              Sign up for the monthly newsletter, the ORBCoN Report, for news, resources, and events.
            </p>
            <NewsletterSignup />
            <p className="text-xs text-blue-200 mt-3 text-center">We respect your privacy. Unsubscribe at any time.</p>
          </div>
        </div>
      </section>

      {/* Quick Access Cards */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center p-8 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-lg transition-all duration-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Resources</h2>
            <p className="text-gray-600 mb-6">
              Access our comprehensive library of guidelines, templates, and educational materials.
            </p>
            <Link
              to="/resources"
              className="inline-block px-6 py-3 bg-[#00467f] text-white rounded-lg font-medium hover:bg-[#003662] transition-colors"
            >
              Browse programs
            </Link>
          </div>

          <div className="text-center p-8 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-lg transition-all duration-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Events</h2>
            <p className="text-gray-600 mb-6">
              Stay updated with upcoming webinars, conferences, and training sessions.
            </p>
            <Link
              to="/events"
              className="inline-block px-6 py-3 bg-[#00467f] text-white rounded-lg font-medium hover:bg-[#003662] transition-colors"
            >
              View Events
            </Link>
          </div>

          <div className="text-center p-8 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-lg transition-all duration-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">About</h2>
            <p className="text-gray-600 mb-6">
              Learn more about ORBCoN and our mission to support transfusion medicine.
            </p>
            <Link
              to="/about"
              className="inline-block px-6 py-3 bg-[#00467f] text-white rounded-lg font-medium hover:bg-[#003662] transition-colors"
            >
              Learn More
            </Link>
          </div>
        </div>
        </div>
      </section>
    </>
  );
}

export default function Home() {
  const { isAuthenticated, isLoading, user } = useAccessLevel();

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <LoadingSkeleton lines={4} />
      </div>
    );
  }

  if (isAuthenticated && user) {
    return <PracticeHome />;
  }

  return <PublicHome />;
}
